import { useState, useCallback } from "react"
import { Text } from "@/components/Primitives/Text"
import { Button } from "@/components/Primitives/Button"
import { Card } from "@/components/Primitives/Card"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"
import { Progress } from "@/components/Primitives/Progress"
import { Question as QuestionType } from "@/types/question"
import { useConfig } from "@/hooks/useConfig"

interface QuestionFormProps {
  questions: QuestionType[]
  answers: Record<string, number>
  onAnswerChange: (questionCode: string, value: number) => void
  onSubmit: (answers: Record<string, number>) => void
  isSubmitting: boolean
  showSkip: boolean
  showProgress: boolean
  className?: string
}

export function QuestionForm({
  questions,
  answers,
  onAnswerChange,
  onSubmit,
  isSubmitting,
  showSkip,
  showProgress,
  className = "",
}: QuestionFormProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const { t } = useConfig()

  const calculateProgress = useCallback(() => {
    if (questions.length === 0) return 0
    return Math.round(((currentStep + 1) / questions.length) * 100)
  }, [currentStep, questions])

  const isCurrentQuestionAnswered = useCallback(() => {
    if (!questions.length) return false
    const currentQuestion = questions[currentStep]
    const currentAnswer = answers[currentQuestion.question_code]
    return currentAnswer !== undefined && currentAnswer > 0
  }, [questions, currentStep, answers])

  const handleNext = useCallback(() => {
    if (currentStep < questions.length - 1) {
      setCurrentStep((prev) => prev + 1)
    } else {
      onSubmit(answers)
    }
  }, [currentStep, questions.length, onSubmit, answers])

  const handleSkipCurrent = useCallback(() => {
    if (currentStep < questions.length - 1) {
      setCurrentStep((prev) => prev + 1)
    } else {
      onSubmit(answers)
    }
  }, [currentStep, questions.length, onSubmit, answers])

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
    }
  }, [currentStep])

  const handleAnswerSelect = useCallback(
    (questionCode: string, value: number) => {
      onAnswerChange(questionCode, value)
    },
    [onAnswerChange],
  )

  if (questions.length === 0) {
    return (
      <div className={`container mx-auto p-6 ${className}`}>
        <Text variant="lead">No questions available.</Text>
      </div>
    )
  }

  const currentQuestion = questions[currentStep]
  const progress = calculateProgress()
  const isLastQuestion = currentStep === questions.length - 1
  const isFirstQuestion = currentStep === 0

  return (
    <div className={`container mx-auto p-0 max-w-2xl ${className}`}>
      <Card className="p-4">
        {showProgress && (
          <div className="p-4 border-b border-neutral-200">
            <Progress value={progress} className="h-1" />
          </div>
        )}

        <div className={showProgress ? "-mt-2" : ""}>
          <MarkdownRenderer
            className="mb-4 text-xl font-semibold"
            content={currentQuestion.question_string}
          />

          {currentQuestion.kind === "Choice" && currentQuestion.choices && (
            <div className="space-y-3">
              {currentQuestion.choices.map((choice) => (
                <div
                  key={choice.choice_value}
                  className="flex items-center border rounded-lg p-4 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() =>
                    handleAnswerSelect(
                      currentQuestion.question_code,
                      choice.choice_value,
                    )
                  }
                >
                  <input
                    type="radio"
                    id={`choice-${choice.choice_value}`}
                    name={currentQuestion.question_code}
                    value={choice.choice_value}
                    checked={
                      answers[currentQuestion.question_code] ===
                      choice.choice_value
                    }
                    readOnly
                    className="mr-3 h-4 w-4 text-purple"
                  />
                  <label
                    htmlFor={`choice-${choice.choice_value}`}
                    className="text-base font-normal cursor-pointer"
                  >
                    {choice.choice_string}
                  </label>
                </div>
              ))}
            </div>
          )}

          {currentQuestion.kind === "Text" && (
            <div className="space-y-3">
              <textarea
                className="w-full p-3 border rounded-lg bg-gray-100 focus:bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows={4}
                placeholder={
                  t("config_question_text_placeholder") ||
                  "Enter your answer..."
                }
                value={answers[currentQuestion.question_code] || ""}
                onChange={(e) =>
                  handleAnswerSelect(
                    currentQuestion.question_code,
                    e.target.value ? 1 : 0,
                  )
                }
              />
            </div>
          )}
        </div>

        <div className="flex flex-row-reverse justify-between border-t pt-4">
          <Button
            onClick={handleNext}
            disabled={!isCurrentQuestionAnswered() || isSubmitting}
            className="bg-purple-500 text-neutral-50"
          >
            {isLastQuestion
              ? isSubmitting
                ? t("config_question_button_submitting")
                : t("config_question_button_finish")
              : t("config_question_button_next")}
          </Button>

          {showSkip && (
            <Button onClick={handleSkipCurrent} variant="outline">
              {t("config_question_button_skip")}
            </Button>
          )}

          <Button
            onClick={handlePrevious}
            hidden={isFirstQuestion}
            variant="outline"
            disabled={isSubmitting}
          >
            {t("config_question_button_previous")}
          </Button>
        </div>
      </Card>
    </div>
  )
}
